import {
  type ExternalLaunchContext,
  ExternalLaunchType,
} from '@/atoms/externalLaunchAuthAtoms';

/**
 * Pure half of the generic SMART App Launch navigation rule; the hooks in
 * `useHiddenNavInSmartOnFhirLaunch` wrap these functions.
 *
 * This rule removes navigation ENTRY POINTS. It is not access control: the
 * routes behind them are not guarded here, and the patient/encounter scope of
 * a launch session is enforced by the backend launch token, not by the
 * sidebar. A route-level guard is tracked separately.
 */

/**
 * Whether the app is running inside a launch from the generic SMART App Launch
 * pipeline: Telus CHR, Cerner, eClinicalWorks, Innowell, Meditech Expanse, nCAS
 * and the Heidi mock EHR, which all arrive through `useGenericEhrLaunch` and
 * write a `GENERIC_SMART_ON_FHIR` launch context. Narrower than
 * `useIsEhrLaunch`.
 *
 * Deliberately excluded: Epic Hyperspace and Haiku (their own launch flow,
 * no launch context), Medtech and SGI Med (their own launch types), the
 * OpenAPI partner launch and Healthdirect.
 */
export const isSmartOnFhirLaunch = (
  launchContext: ExternalLaunchContext | null | undefined
): boolean => launchContext?.launchType === ExternalLaunchType.GENERIC_SMART_ON_FHIR;

/**
 * Primary-nav entry points a generic SMART launch hides because they lead
 * away from the launched patient and encounter: the Home item, the Tasks
 * item, the Agent Home secondary panel, the "+" create button (its menu
 * opens Tasks, a Heidi 2 chat and routines) and the workspace switcher. The
 * sessions list, Calls and Patients are already hidden for every EHR launch
 * by `useIsEhrLaunch`, so they are not listed here.
 *
 * `create` and `workspaceSwitcher` exist because the generic launch does not
 * set the Redux auth provider to FHIR (only the jotai launch atoms), so the
 * sidebar's FHIR-auth suppressions, which do cover Epic, Medtech and SGI Med,
 * skip Telus and Cerner.
 */
export type SmartOnFhirHiddenNavItem =
  | 'home'
  | 'tasks'
  | 'agentHome'
  | 'create'
  | 'workspaceSwitcher';

export const NO_HIDDEN_NAV: ReadonlySet<SmartOnFhirHiddenNavItem> = new Set();

const DEFAULT_HIDDEN_NAV: ReadonlySet<SmartOnFhirHiddenNavItem> = new Set([
  'home',
  'tasks',
  'agentHome',
  'create',
  'workspaceSwitcher',
]);

export type HiddenNavOverrides = ReadonlyMap<
  string,
  ReadonlySet<SmartOnFhirHiddenNavItem>
>;

/**
 * Per-provider opt-outs, keyed by the `ehrProvider` the launch context
 * carries (the `EHRProvider` display value, e.g. "Cerner EHR Launch"). A Map
 * rather than a plain object so a provider name can never collide with
 * `Object.prototype`. Empty today: every live SMART connector wants the full
 * set.
 */
const HIDDEN_NAV_BY_PROVIDER: HiddenNavOverrides = new Map();

export const getHiddenNavInSmartOnFhirLaunch = (
  ehrProvider: string | null | undefined,
  overrides: HiddenNavOverrides = HIDDEN_NAV_BY_PROVIDER
): ReadonlySet<SmartOnFhirHiddenNavItem> =>
  (ehrProvider ? overrides.get(ehrProvider) : undefined) ?? DEFAULT_HIDDEN_NAV;

export const resolveHiddenNavInSmartOnFhirLaunch = (
  launchContext: ExternalLaunchContext | null | undefined
): ReadonlySet<SmartOnFhirHiddenNavItem> =>
  isSmartOnFhirLaunch(launchContext)
    ? getHiddenNavInSmartOnFhirLaunch(launchContext?.ehrProvider)
    : NO_HIDDEN_NAV;
